import React from "react";
import { defaultColors, styled } from "../../../stitches.config";
import { bodyLarge, h3, body, h4 } from "../../../redesign/commonStyles/Fonts";
import Avatar from "../../../redesign/atoms/Avatar";
import { BlockquoteElement } from "../../../components/Article/Parts/Slate/types";
import RenderSlateChildren from "../../../components/Article/Parts/Slate/RenderSlateChildren";
import { withThemeContext } from "../../../redesign/components/StitchesThemeProvider";
import { ReactComponent as BackgroundInnEmpty } from "../../../redesign/assets/innpolandRedesign/footer-inn-empty-gray.svg?react"

type ExtendedQuoteProps = {
  text?: string;
  authorName?: string;
  time?: string;
  day?: string;
  src?: string;
  url?: string;
};

type QuoteProps = {
  element: BlockquoteElement & ExtendedQuoteProps;
  isDarkMode?: boolean;
};

const Quote: React.FC<QuoteProps> = ({
  element,
  isDarkMode,
  ...props
}) => {
  const isDesktop = window.innerWidth >= 992;
  const {
    author,
    authorAbout,
    children,
    text,
    url,
    src,
    authorName,
    day,
    time,
  } = element;
  return (
    <StyledQuote variant={isDarkMode ? "dark" : undefined}>
      <StyledQuoteIcon>
        <svg viewBox="0 0 88 88" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_4145_15939)">
            <path d="M84.3163 8V13.2814C76.8044 16.012 70.9176 20.3234 66.6561 26.2156C62.3945 32.0359 60.2637 38.1078 60.2637 44.4311C60.2637 45.7964 60.4804 46.8024 60.9138 47.4491C61.2027 47.8802 61.5278 48.0958 61.8889 48.0958C62.2501 48.0958 62.7918 47.8443 63.5141 47.3413C65.8254 45.6886 68.7508 44.8623 72.29 44.8623C76.4071 44.8623 80.0547 46.5509 83.2328 49.9281C86.4109 53.2335 88 57.1856 88 61.7844C88 66.5988 86.1581 70.8383 82.4744 74.503C78.8629 78.1677 74.4569 80 69.2564 80C63.1891 80 57.9524 77.521 53.5464 72.5629C49.1404 67.6048 46.9373 60.9581 46.9373 52.6228C46.9373 42.9222 49.9349 34.2275 55.93 26.5389C61.925 18.8503 71.3871 12.6707 84.3163 8ZM37.3789 8V13.2814C29.867 16.012 23.9803 20.3234 19.7187 26.2156C15.4572 32.0359 13.3264 38.1078 13.3264 44.4311C13.3264 45.7964 13.5431 46.8024 13.9765 47.4491C14.2654 47.8802 14.5904 48.0958 14.9516 48.0958C15.3127 48.0958 15.8544 47.8443 16.5767 47.3413C18.8881 45.6886 21.8134 44.8623 25.3527 44.8623C29.4698 44.8623 33.1174 46.5509 36.2955 49.9281C39.4736 53.2335 41.0627 57.1856 41.0627 61.7844C41.0627 66.5988 39.2208 70.8383 35.5371 74.503C31.9256 78.1677 27.5196 80 22.319 80C16.2517 80 11.015 77.521 6.60903 72.5629C2.20301 67.6048 0 60.9581 0 52.6228C0 42.9222 2.99754 34.2275 8.99261 26.5389C14.9877 18.8503 24.4498 12.6707 37.3789 8Z" />
          </g>
          <defs>
            <clipPath id="clip0_4145_15939">
              <rect width="88" height="88" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </StyledQuoteIcon>
      <div>
        {isDesktop ? (
          <span className={h3()}>
            <RenderSlateChildren element={children} />
          </span>
        ) : (
          <span className={h4()}>
            <RenderSlateChildren element={children} />
          </span>
        )}
        <StyledQuoteAuthor>
          {src && url && <Avatar src={src} url={url} />}
          <div>
            <p className={bodyLarge()}>{author}</p>
            {authorAbout && (
              <p className={body()}>
                <span>{authorAbout}</span>
              </p>
            )}
            {day && time && (
              <p className={body()}>
                <span>
                  {day}, {time}
                </span>
              </p>
            )}
          </div>
        </StyledQuoteAuthor>
      </div>
      <StyledQuoteLogoIcon>
        <BackgroundInnEmpty />
      </StyledQuoteLogoIcon>
    </StyledQuote>
  );
};

const StyledQuote = styled("div", {
  background: "$grayscale100",
  position: "relative",
  padding: "45px 64px 48px 24px",
  margin: "24px 0 72px 0",
  "@bp4": {
    padding: "72px 152px 72px 64px",
    margin: "32px 0",
  },
  "& span": {
    margin: 0,
    color: defaultColors.grayscale0,
  },
  variants: {
    variant: {
      dark: {
        background: "#232323",
      },
    },
  },
});

const StyledQuoteIcon = styled("div", {
  width: "64px",
  height: "58px",
  margin: "0 0 30px 0",
  "@bp4": {
    width: "88px",
    height: "82px",
    margin: "0 0 40px 0",
  },
  "svg": {
    fill: "$primary",
    width: "64px",
    height: "58px",
    "@bp4": {
      width: "88px",
      height: "82px",
    },
  }
});

const StyledQuoteLogoIcon = styled("div", {
  position: "absolute",
  right: "0px",
  bottom: "0px",
  width: "64px",
  height: "64px",
  flexShrink: "0",
  "@bp4": {
    right: "-40px",
    bottom: "40px",
    height: "137px",
    width: "200px",
  },
  "svg": {
    stroke: "$primary",
    transform: "rotate(-90deg)",
  }
});

const StyledQuoteAuthor = styled("div", {
  marginTop: "24px",
  display: "flex",
  alignItems: "center",
  color: defaultColors.grayscale0,
  "& .avatar": {
    width: "42px",
    height: "42px",
    marginRight: "12px",
  },
  "& span": {
    color: "$grayscale75",
  },
  "& p": {
    marginTop: "0px",
    marginBottom: "0px",
  },
});


export default withThemeContext(Quote);
